import {
    SavedRule,
    MappingRow,
    HandlerOption,
    HandlerRegistryEntry,
    ConditionRow,
    ConfigureRuleFormState,
    RuleDetailsSummary,
    ApprovalFormState,
    SubmissionEntry,
    ApprovalAuditLogEntry,
    CreateHandlerFormState,
    ActivationRuleRow,
    ConfiguredEwsSignal,
    ConfiguredApprover,
    ResourceOption,
    RuleTestSampleResult
} from './types';

export const METRIC_DOMAIN_OPTIONS: HandlerOption[] = [];

export const COMPONENT_FILTER_OPTIONS: HandlerOption[] = [
    { value: 'api', label: 'API' },
    { value: 'adf', label: 'ADF' },
    { value: 'databricks', label: 'Databricks' },
    { value: 'synapse', label: 'Synapse' }
];

export const LAYER_TOOL_OPTIONS: HandlerOption[] = [
    { value: 'bronze', label: 'Bronze' },
    { value: 'silver', label: 'Silver' },
    { value: 'gold', label: 'Gold' }
];

export const METRIC_KPI_OPTIONS: HandlerOption[] = [];
export const DATA_SOURCE_OPTIONS: HandlerOption[] = [];

export const RISK_LEVEL_OPTIONS = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'critical', label: 'Critical' }
];

export const COMPONENT_OPERATOR_OPTIONS = [
    { value: '', label: ' ' },
    { value: '==', label: '==' },
    { value: '!=', label: '!=' },
    { value: '>', label: '>' },
    { value: '<', label: '<' },
    { value: '>=', label: '>=' },
    { value: '<=', label: '<=' },
    { value: 'GREATER_THAN', label: '>' },
    { value: 'LESS_THAN', label: '<' },
    { value: 'GREATER_THAN_OR_EQUAL', label: '>=' },
    { value: 'LESS_THAN_OR_EQUAL', label: '<=' },
    { value: 'EQUALS', label: '==' },
    { value: 'NOT_EQUALS', label: '!=' }
];

export const METRIC_KPI_LABELS: Record<string, string> = {
    volume: 'Volume / Record Count',
    latency: 'Execution Latency',
    quality: 'Data Quality Error Rate',
    schema: 'Schema Integrity'
};

export const DATA_SOURCE_LABELS: Record<string, string> = {
    source1: 'Ingestion Metrics',
    source2: 'Pipeline Logs',
    source3: 'DQ Telemetry'
};

export const DATAOPS_ADMIN_ROLES = ['Data Operations Admin', 'PepWise System User'];
export const DATAOPS_VIEWER_ROLES = ['Data Operations Admin', 'Data Operations User', 'PepWise System User'];
export const ALLOWED_APPROVAL_CONFIG_ROLES = ['Data Operations Admin', 'PepWise System User'];

export const ENFORCE_APPROVAL_BEFORE_ACTIVATION = true;
export const ENFORCE_ASSIGNED_REVIEWER = true;

export const RULE_NAME_MAX_LENGTH = 100;
export const EWS_SIGNAL_NAME_MAX_LENGTH = 100;
export const SAVE_SIMULATED_DELAY_MS = 200;

export const CONFIGURED_EWS_SIGNALS: ConfiguredEwsSignal[] = [];

export const initialSavedRules: SavedRule[] = [];

export const initialMappingData: MappingRow[] = [];

export const initialHandlerTypeOptions: HandlerOption[] = [
    { value: 'pepwise', label: 'Pepwise' }
];

export const initialHandlerOptions: HandlerOption[] = [
    { value: 'pepwise_db', label: 'Pepwise DB' }
];

export const HANDLER_CATEGORY_OPTIONS: HandlerOption[] = [
    { value: 'all', label: 'All' },
    { value: 'sql', label: 'Database / SQL' },
    { value: 'agents', label: 'Agents' },
    { value: 'python', label: 'Python' },
    { value: 'api', label: 'API' },
    { value: 'other', label: 'Other' }
];

export const initialRegistry: HandlerRegistryEntry[] = [];

// --- Configure Rules tab ---

export const BUSINESS_DOMAIN_OPTIONS: HandlerOption[] = [];
export const SECTOR_OPTIONS: HandlerOption[] = [];
export const PROGRAM_OPTIONS: HandlerOption[] = [];

export const RESOURCE_TYPE_OPTIONS: HandlerOption[] = [
    { value: 'adf', label: 'Azure Data Factory (ADF)' },
    { value: 'adb', label: 'Azure Databricks (ADB)' },
    { value: 'adls', label: 'Azure Data Lake Storage (ADLS)' }
];

export const DEFAULT_RESOURCE_OPTIONS: ResourceOption[] = [];

export const RESOURCE_OPTIONS: ResourceOption[] = [];

export const CONDITION_FIELD_OPTIONS = [
    { value: 'growth-rule', label: 'Growth Rule' },
    { value: 'volume', label: 'Volume' },
    { value: 'latency', label: 'Latency' },
    { value: 'quality', label: 'Quality' }
];

export const emptyConditionRow = (): ConditionRow => ({ field: '', component: '', value: '', unit: '' });

export const SCHEDULE_UNIT_OPTIONS: HandlerOption[] = [
    { value: 'm', label: 'Minutes (m)' },
    { value: 'h', label: 'Hours (h)' },
    { value: 'd', label: 'Days (d)' }
];

export const initialConfigureRuleForm: ConfigureRuleFormState = {
    businessDomain: '',
    sector: '',
    program: '',
    resourceType: 'adf',
    resources: [],
    environment: 'uat',
    evalWindow: '14d',
    checkInterval: '1h',
    coolDown: '30m'
};

export const SAMPLE_RULE_TEST_RESULTS: RuleTestSampleResult[] = [];

export const emptyRuleDetailsSummary: RuleDetailsSummary = {
    ruleName: '',
    signal: '',
    domainComponentLayer: '',
    riskLevel: '',
    status: '',
    metricKPI: '',
    dataSource: '',
    detectionLogic: '',
    handlerAgent: '',
    notes: '',
    createdAt: ''
};

// --- Approval tab ---

export const ROLE_TITLE_OPTIONS = [
    { value: 'domain-owner', label: 'Domain Owner' },
    { value: 'platform-owner', label: 'Platform Owner' },
    { value: 'security-lead', label: 'Security Lead' },
    { value: 'data-ops-admin', label: 'Data Operations Admin' }
];

export const SLA_OPTIONS = [
    { value: '24h', label: '24 Hours' },
    { value: '48h', label: '48 Hours' },
    { value: '72h', label: '72 Hours' }
];

export const APPROVAL_MODE_OPTIONS = [
    { value: 'sequential', label: 'Sequential' },
    { value: 'parallel', label: 'Parallel' }
];

export const REVIEWING_AS_OPTIONS = [
    { value: '1', label: 'Level 1 - Domain Owner' },
    { value: '2', label: 'Level 2 - Platform/Security' }
];

export const CONFIGURED_APPROVERS: ConfiguredApprover[] = [];
export const L1_APPROVER_OPTIONS: HandlerOption[] = [];
export const L2_APPROVER_OPTIONS: HandlerOption[] = [];

export const DEFAULT_APPROVAL_CONFIG: ApprovalFormState = {
    level1: { approverEmail: '', roleTitle: '', sla: '24h' },
    level2: { approverEmail: '', roleTitle: '', sla: '48h' },
    approvalMode: 'sequential',
    escalationContact: '',
    postApprovalNotify: '',
    submitterNotes: ''
};

export const initialApprovalForm: ApprovalFormState = DEFAULT_APPROVAL_CONFIG;

export const initialSubmissions: SubmissionEntry[] = [];

export const initialApprovalAuditLog: ApprovalAuditLogEntry[] = [];

// --- Create New Handler modal ---

export const HANDLER_VERSION_OPTIONS: HandlerOption[] = [
    { value: 'v1.0.0', label: 'v1.0.0' },
    { value: 'v1.5.0', label: 'v1.5.0' },
    { value: 'v2.0.0', label: 'v2.0.0' },
    { value: 'v2.1.0', label: 'v2.1.0' }
];

export const HANDLER_EXECUTOR_OPTIONS: HandlerOption[] = [
    { value: 'python', label: 'Python Runtime' },
    { value: 'api', label: 'API Runtime' },
    { value: 'sql', label: 'SQL Engine' },
    { value: 'batch', label: 'Batch Job' },
    { value: 'other', label: 'Other' }
];

export const initialCreateHandlerForm: CreateHandlerFormState = {
    handlerName: '',
    handlerType: '',
    version: '',
    executor: '',
    timeout: '',
    dataSources: [],
    outputMetric: '',
    description: '',
    tags: ''
};

// --- Activation tab ---

export const ACTIVATION_STATUS_OPTIONS = [
    { value: 'all', label: 'All' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' }
];

export const initialActivationRules: ActivationRuleRow[] = [];
